<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { DateTime } from 'luxon'
import { DeleteDialog } from '@haskoning/vue-component-library'

import { useCalculationStore } from '@/stores/calculation'
import { useCalculationPermissions } from '@/composables/useCalculationPermissions'
import type { CalculationResultFile } from '@/stores/type/calculation.type'
import OutputAccessDialog from '@/components/calculation-result/OutputAccessDialog.vue'

const calculationStore = useCalculationStore()
const {
  currentItem,
  resultFiles,
  resultStorageCost,
  resultFilesLoading,
  resultFileDownloadLoading,
  resultFilesZipDownloadLoading,
  resultFileDeleteLoading,
  resultFolderDeleteLoading,
  resultDownloadError
} = storeToRefs(calculationStore)
const { canDeleteCalculation } = useCalculationPermissions()

const MAX_OUTPUT_ZIP_SIZE_BYTES = 2 * 1024 * 1024 * 1024

type ResultFileViewMode = 'flat' | 'folders'

type FolderRow = {
  type: 'folder'
  key: string
  name: string
  path: string[]
  size: number
  storageCost: number | null
  modified: string | null
  fileCount: number
}

type FileRow = {
  type: 'file'
  key: string
  name: string
  file: CalculationResultFile
}

type ResultFileRow = FolderRow | FileRow
type DeleteTarget =
  | {
      type: 'file'
      name: string
      file: CalculationResultFile
    }
  | {
      type: 'folder'
      name: string
      path: string[]
    }

const viewMode = ref<ResultFileViewMode>('folders')
const currentPath = ref<string[]>([])
const deleteDialog = ref(false)
const deleteTarget = ref<DeleteTarget | null>(null)
const accessDialog = ref(false)
const accessPath = ref<string[]>([])

const canManageOutput = canDeleteCalculation

const flatRows = computed<FileRow[]>(() =>
  resultFiles.value.map((file) => ({
    type: 'file',
    key: file.blob_name,
    name: file.name,
    file
  }))
)

const folderRows = computed<ResultFileRow[]>(() => {
  const folders = new Map<string, FolderRow>()
  const files: FileRow[] = []

  for (const file of resultFiles.value) {
    const parts = pathParts(file.name)
    if (!isInCurrentPath(parts)) continue

    const remainingParts = parts.slice(currentPath.value.length)
    if (remainingParts.length <= 0) continue

    if (remainingParts.length > 1) {
      const folderName = remainingParts[0]
      const folderPath = [...currentPath.value, folderName]
      const key = folderPath.join('/')
      const existingFolder = folders.get(key)
      const nextSize = file.size ?? 0
      const nextStorageCost = file.estimated_monthly_storage_cost
      const nextModified = file.last_modified ?? file.created ?? null

      if (existingFolder) {
        existingFolder.size += nextSize
        existingFolder.storageCost = addStorageCost(existingFolder.storageCost, nextStorageCost)
        existingFolder.fileCount += 1
        existingFolder.modified = latestDate(existingFolder.modified, nextModified)
      } else {
        folders.set(key, {
          type: 'folder',
          key: `folder-${key}`,
          name: folderName,
          path: folderPath,
          size: nextSize,
          storageCost: nextStorageCost ?? null,
          modified: nextModified,
          fileCount: 1
        })
      }
    } else {
      files.push({
        type: 'file',
        key: file.blob_name,
        name: remainingParts[0],
        file
      })
    }
  }

  return [
    ...Array.from(folders.values()).sort((a, b) => a.name.localeCompare(b.name)),
    ...files.sort((a, b) => a.name.localeCompare(b.name))
  ]
})

const isFolderView = computed(() => viewMode.value === 'folders')
const displayedRows = computed(() => (isFolderView.value ? folderRows.value : flatRows.value))
const headers = computed(() => [
  {
    title: isFolderView.value ? 'Name' : 'File',
    key: 'name',
    align: 'start' as const
  },
  {
    title: isFolderView.value ? 'Created/modified' : 'Created',
    key: 'modified',
    align: 'start' as const,
    sortable: false
  },
  { title: 'Size', key: 'size', align: 'start' as const, sortable: false },
  {
    title: 'Storage/month',
    key: 'storageCost',
    align: 'start' as const,
    sortable: false
  },
  { title: 'Actions', key: 'actions', align: 'center' as const, sortable: false }
])
const currentZipPath = computed(() => (isFolderView.value ? currentPath.value : []))
const currentZipFiles = computed(() => filesForPath(currentZipPath.value))
const currentZipSize = computed(() => totalSize(currentZipFiles.value))
const currentZipDisabledReason = computed(() =>
  zipDisabledReason(currentZipSize.value, currentZipFiles.value.length)
)
const currentZipDownloadKey = computed(() => zipDownloadKey(currentZipPath.value))
const isCurrentZipDownloading = computed(() => {
  return resultFilesZipDownloadLoading.value === currentZipDownloadKey.value
})
const isDeleting = computed(() => {
  return !!resultFileDeleteLoading.value || !!resultFolderDeleteLoading.value
})
const storageCostCurrency = computed(() => resultStorageCost.value?.currency_code ?? 'EUR')
const storageCostLabel = computed(() =>
  formatMonthlyStorageCost(resultStorageCost.value?.estimated_monthly_cost)
)
const storageCostTooltip = computed(() => {
  const cost = resultStorageCost.value
  if (!cost) return 'Storage cost has not been loaded yet.'
  if (cost.estimated_monthly_cost === null || cost.estimated_monthly_cost === undefined) {
    return 'No Azure retail storage price was found for this storage account configuration.'
  }

  const parts = [
    [cost.product_name, cost.sku_name].filter(Boolean).join(' '),
    cost.meter_name,
    cost.price_per_gb_month !== null && cost.price_per_gb_month !== undefined
      ? `${formatMoney(cost.price_per_gb_month, storageCostCurrency.value)}/GB-month`
      : null,
    formatFileSize(cost.total_size_bytes)
  ].filter(Boolean)
  return parts.join(' · ')
})

const fetchFiles = async () => {
  if (currentItem.value?.id) {
    await calculationStore.getResultFiles(currentItem.value.id)
  }
}

const downloadFile = async (file: CalculationResultFile) => {
  if (currentItem.value?.id) {
    await calculationStore.downloadResultFile(currentItem.value.id, file)
  }
}

const downloadOutputZip = async (pathParts: string[] = currentZipPath.value) => {
  if (!currentItem.value?.id) return
  const files = filesForPath(pathParts)
  const disabledReason = zipDisabledReason(totalSize(files), files.length)
  if (disabledReason) return

  await calculationStore.downloadResultFilesZip(currentItem.value.id, pathParts)
}

const openAccessDialog = () => {
  accessPath.value = [...currentZipPath.value]
  accessDialog.value = true
}

const openDeleteFileDialog = (file: CalculationResultFile) => {
  deleteTarget.value = {
    type: 'file',
    name: file.name,
    file
  }
  deleteDialog.value = true
}

const openDeleteFolderDialog = (row: ResultFileRow) => {
  if (row.type !== 'folder') return

  deleteTarget.value = {
    type: 'folder',
    name: row.path.join('/'),
    path: row.path
  }
  deleteDialog.value = true
}

const deleteOutputTarget = async () => {
  if (!currentItem.value?.id || !deleteTarget.value) return

  const target = deleteTarget.value
  const deleted =
    target.type === 'file'
      ? await calculationStore.deleteResultFile(currentItem.value.id, target.file)
      : await calculationStore.deleteResultFolder(currentItem.value.id, target.path)

  if (!deleted) return

  if (target.type === 'folder' && isInDeletedFolder(target.path)) {
    currentPath.value = target.path.slice(0, -1)
  }

  deleteDialog.value = false
  deleteTarget.value = null
}

const openFolder = (row: ResultFileRow) => {
  if (row.type !== 'folder') return
  currentPath.value = row.path
}

const goToPath = (index: number) => {
  currentPath.value = currentPath.value.slice(0, index)
}

const pathParts = (path: string) => {
  return path.split('/').filter(Boolean)
}

const isInCurrentPath = (parts: string[]) => {
  return currentPath.value.every((part, index) => parts[index] === part)
}

const filesForPath = (path: string[]) => {
  return resultFiles.value.filter((file) => {
    const parts = pathParts(file.name)
    return path.every((part, index) => parts[index] === part) && parts.length > path.length
  })
}

const totalSize = (files: CalculationResultFile[]) => {
  return files.reduce((sum, file) => sum + (file.size ?? 0), 0)
}

const addStorageCost = (currentCost: number | null, nextCost?: number | null) => {
  if (nextCost === null || nextCost === undefined) return currentCost
  return (currentCost ?? 0) + nextCost
}

const zipDownloadKey = (path: string[]) => {
  return path.join('/') || '__root__'
}

const zipDisabledReason = (size: number, fileCount: number) => {
  if (fileCount <= 0) return 'No output files found.'
  if (size > MAX_OUTPUT_ZIP_SIZE_BYTES) {
    return 'Cannot download ZIP because the selected output exceeds the 2 GB limit.'
  }
  return ''
}

const latestDate = (currentDate?: string | null, nextDate?: string | null) => {
  if (!currentDate) return nextDate ?? null
  if (!nextDate) return currentDate

  return DateTime.fromISO(nextDate) > DateTime.fromISO(currentDate) ? nextDate : currentDate
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '-'
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED)
}

const formatFileSize = (size?: number | null) => {
  if (size === null || size === undefined) return '-'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`
  return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`
}

const formatMoney = (value?: number | null, currency = storageCostCurrency.value) => {
  if (value === null || value === undefined) return '-'
  const maximumFractionDigits = value < 0.01 ? 6 : value < 1 ? 4 : 2
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits
  }).format(value)
}

const formatMonthlyStorageCost = (value?: number | null) => {
  const formatted = formatMoney(value, storageCostCurrency.value)
  return formatted === '-' ? '-' : `${formatted}/month`
}

const rowStorageCost = (row: ResultFileRow) => {
  return row.type === 'folder' ? row.storageCost : row.file.estimated_monthly_storage_cost
}

const rowModified = (row: ResultFileRow) => {
  return row.type === 'folder' ? row.modified : (row.file.created ?? row.file.last_modified)
}

const rowSize = (row: ResultFileRow) => {
  if (row.type === 'folder') {
    return `${formatFileSize(row.size)} · ${folderFileCount(row.fileCount)}`
  }
  return formatFileSize(row.file.size)
}

const folderFileCount = (count: number) => {
  return count === 1 ? '1 file' : `${count} files`
}

const folderZipDisabledReason = (row: ResultFileRow) => {
  if (row.type !== 'folder') return ''
  return zipDisabledReason(row.size, row.fileCount)
}

const isFolderZipDownloading = (row: ResultFileRow) => {
  if (row.type !== 'folder') return false
  return resultFilesZipDownloadLoading.value === zipDownloadKey(row.path)
}

const isFolderDeleting = (row: ResultFileRow) => {
  if (row.type !== 'folder') return false
  return resultFolderDeleteLoading.value === zipDownloadKey(row.path)
}

const isInDeletedFolder = (folderPath: string[]) => {
  return folderPath.every((part, index) => currentPath.value[index] === part)
}

onMounted(fetchFiles)

watch(
  () => currentItem.value?.id,
  () => {
    accessDialog.value = false
  }
)
</script>

<template>
  <v-card class="mx-2 mt-4" border="opacity-25 sm" elevation="0" flat>
    <v-toolbar density="compact" color="transparent" class="output-toolbar">
      <v-toolbar-title class="text-subtitle-1 font-weight-medium">Output files</v-toolbar-title>
      <v-spacer />
      <v-tooltip :text="storageCostTooltip" location="top">
        <template #activator="{ props }">
          <v-chip
            v-bind="props"
            class="mr-2 storage-cost-chip"
            prepend-icon="mdi-database-outline"
            size="small"
            variant="tonal"
          >
            {{ storageCostLabel }}
          </v-chip>
        </template>
      </v-tooltip>
      <v-btn-toggle
        v-model="viewMode"
        class="mr-2"
        density="compact"
        divided
        mandatory
        variant="outlined"
      >
        <v-btn value="flat" prepend-icon="mdi-format-list-bulleted" class="text-none"> Flat </v-btn>
        <v-btn value="folders" prepend-icon="mdi-folder-outline" class="text-none"> Folders </v-btn>
      </v-btn-toggle>
      <v-btn
        class="text-none mr-2"
        prepend-icon="mdi-link-variant"
        variant="tonal"
        :disabled="!currentZipFiles.length || resultFilesLoading"
        @click="openAccessDialog"
      >
        Create access link
      </v-btn>
      <v-tooltip
        :disabled="!currentZipDisabledReason"
        :text="currentZipDisabledReason"
        location="top"
      >
        <template #activator="{ props }">
          <span v-bind="props" class="mr-2">
            <v-btn
              class="text-none"
              prepend-icon="mdi-folder-download-outline"
              variant="tonal"
              :disabled="!!currentZipDisabledReason"
              :loading="isCurrentZipDownloading"
              @click="downloadOutputZip()"
            >
              Download ZIP
            </v-btn>
          </span>
        </template>
      </v-tooltip>
      <v-btn icon="mdi-refresh" variant="text" :loading="resultFilesLoading" @click="fetchFiles" />
    </v-toolbar>

    <v-progress-linear v-if="resultFilesLoading" indeterminate color="primary" />
    <v-alert
      v-if="resultDownloadError"
      type="error"
      variant="tonal"
      class="ma-3"
      closable
      @click:close="resultDownloadError = ''"
    >
      {{ resultDownloadError }}
    </v-alert>
    <div
      v-if="resultFilesZipDownloadLoading"
      role="status"
      aria-live="polite"
      class="mx-4 my-2 text-body-2"
    >
      Preparing ZIP… Your download will start when it is ready.
    </div>

    <div v-if="isFolderView" class="folder-breadcrumbs">
      <v-btn
        class="text-none"
        density="comfortable"
        prepend-icon="mdi-folder-home-outline"
        variant="text"
        @click="goToPath(0)"
      >
        Output files
      </v-btn>
      <template v-for="(part, index) in currentPath" :key="part + index">
        <v-icon icon="mdi-chevron-right" size="small" />
        <v-btn class="text-none" density="comfortable" variant="text" @click="goToPath(index + 1)">
          {{ part }}
        </v-btn>
      </template>
    </div>

    <v-data-table
      :headers="headers"
      :items="displayedRows"
      :items-per-page="-1"
      density="comfortable"
      hide-default-footer
      item-value="key"
    >
      <template v-slot:[`item.name`]="{ item: row }">
        <button
          v-if="row.type === 'folder'"
          class="file-name-cell folder-name-button"
          type="button"
          @click="openFolder(row)"
        >
          <v-icon icon="mdi-folder-outline" size="small" />
          <span>{{ row.name }}</span>
        </button>
        <span v-else class="file-name-cell">
          <v-icon v-if="isFolderView" icon="mdi-file-outline" size="small" />
          <span>{{ row.name }}</span>
        </span>
      </template>
      <template v-slot:[`item.modified`]="{ item: row }">
        {{ formatDate(rowModified(row)) }}
      </template>
      <template v-slot:[`item.size`]="{ item: row }">{{ rowSize(row) }}</template>
      <template v-slot:[`item.storageCost`]="{ item: row }">
        <span class="storage-cost-cell">
          {{ formatMonthlyStorageCost(rowStorageCost(row)) }}
        </span>
      </template>
      <template v-slot:[`item.actions`]="{ item: row }">
        <div class="output-actions">
          <template v-if="row.type === 'folder'">
            <v-tooltip
              :disabled="!folderZipDisabledReason(row)"
              :text="folderZipDisabledReason(row)"
              location="top"
            >
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-btn
                    icon="mdi-folder-download-outline"
                    variant="text"
                    density="comfortable"
                    :disabled="!!folderZipDisabledReason(row)"
                    :loading="isFolderZipDownloading(row)"
                    @click.stop="downloadOutputZip(row.path)"
                  />
                </span>
              </template>
            </v-tooltip>
            <v-btn
              v-if="canManageOutput"
              icon="mdi-delete-outline"
              color="error"
              variant="text"
              density="comfortable"
              :loading="isFolderDeleting(row)"
              @click.stop="openDeleteFolderDialog(row)"
            />
          </template>
          <template v-else>
            <v-btn
              icon="mdi-download"
              variant="text"
              density="comfortable"
              :loading="resultFileDownloadLoading === row.file.blob_name"
              @click.stop="downloadFile(row.file)"
            />
            <v-btn
              v-if="canManageOutput"
              icon="mdi-delete-outline"
              color="error"
              variant="text"
              density="comfortable"
              :loading="resultFileDeleteLoading === row.file.blob_name"
              @click.stop="openDeleteFileDialog(row.file)"
            />
          </template>
        </div>
      </template>
      <template #no-data>
        <div v-if="!resultFilesLoading" class="py-8 text-medium-emphasis">
          No output files found.
        </div>
      </template>
    </v-data-table>

    <DeleteDialog
      v-if="deleteTarget"
      v-model="deleteDialog"
      :loading="isDeleting"
      :name="deleteTarget.name"
      :item="deleteTarget"
      @set-delete-item="deleteTarget = $event"
      @delete="deleteOutputTarget"
    />
    <OutputAccessDialog
      v-if="accessDialog && currentItem"
      v-model="accessDialog"
      :calculation-id="currentItem.id"
      :path="accessPath"
    />
  </v-card>
</template>

<style scoped>
.output-toolbar :deep(.v-toolbar__content) {
  flex-wrap: wrap;
  gap: 8px 0;
  height: auto !important;
  min-height: 56px;
  padding: 8px 0;
}

.output-toolbar {
  height: auto !important;
}

.folder-breadcrumbs {
  align-items: center;
  display: flex;
  gap: 2px;
  min-height: 44px;
  overflow-x: auto;
  padding: 0 16px 4px;
  white-space: nowrap;
}

.file-name-cell {
  align-items: center;
  background: none;
  border: 0;
  color: inherit;
  display: inline-flex;
  font: inherit;
  gap: 8px;
  min-width: 0;
  padding: 0;
  text-align: left;
}

.folder-name-button {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}

.output-actions {
  white-space: nowrap;
}

.storage-cost-cell,
.storage-cost-chip {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
